/**
 * useAudioRecorder — minimal MediaRecorder wrapper for voice login.
 * Returns: { isRecording, recordingTime, startRecording, stopRecording, cancelRecording, error }
 */
import { useState, useRef, useCallback, useEffect } from 'react';

export function useAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const stopResolverRef = useRef(null);

  const cleanupTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Microphone API not available in this browser.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      streamRef.current = stream;

      // Pick best supported MIME
      const mimeCandidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
      const mimeType = mimeCandidates.find((m) => window.MediaRecorder && window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(m)) || '';
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        chunksRef.current = [];
        cleanupTimer();
        stopStream();
        setIsRecording(false);
        if (stopResolverRef.current) {
          stopResolverRef.current(blob);
          stopResolverRef.current = null;
        }
      };

      mr.start(250);
      setIsRecording(true);
      setRecordingTime(0);
      const t0 = Date.now();
      intervalRef.current = setInterval(() => {
        setRecordingTime(Math.floor((Date.now() - t0) / 1000));
      }, 250);
    } catch (err) {
      setError(err?.message || 'Microphone access denied.');
      setIsRecording(false);
      stopStream();
      throw err;
    }
  }, [cleanupTimer, stopStream]);

  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        resolve(new Blob());
        return;
      }
      stopResolverRef.current = resolve;
      try { mediaRecorderRef.current.stop(); } catch { resolve(new Blob()); }
    });
  }, []);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      stopResolverRef.current = null;
      try { mediaRecorderRef.current.stop(); } catch { /* noop */ }
    }
    chunksRef.current = [];
    cleanupTimer();
    stopStream();
    setIsRecording(false);
    setRecordingTime(0);
  }, [cleanupTimer, stopStream]);

  useEffect(() => () => { cleanupTimer(); stopStream(); }, [cleanupTimer, stopStream]);

  return { isRecording, recordingTime, startRecording, stopRecording, cancelRecording, error };
}

export default useAudioRecorder;

/** Convert Blob → base64 data URL (resolves to string). */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}
